<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      v-if="canRunInvoiceGeneration"
      :icon="AddIcon"
      :label="$t('invoicing.generateInvoices')"
      :busy="generating"
      @click="generateInvoices"
    />
    <action
      v-if="canRunInvoiceGeneration"
      :icon="AddIcon"
      :label="$t('invoicing.importInvoices')"
      :busy="importing"
      @click="importInvoices"
    />
  </toolbar>
  <div class="main-area h-full">
    <div class="ml-12">
      <div class="table">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :actions="actions"
          :visible-columns="visibleColumns"
        >
          <template #invoiceStatus="{ value, item }">
            <span
              v-if="item?.invoiceStatus"
              class="status-badge"
              :class="statusBadgeClass(String(item.invoiceStatus))"
              :style="statusBadgeStyle(String(item.invoiceStatus))"
              data-darkreader-ignore
            >
              {{ formatInvoiceStatus(String(item.invoiceStatus)) }}
            </span>
            <span
              v-else
              class="status-badge"
              :class="statusBadgeClass(String(value ?? 'unknown'))"
              :style="statusBadgeStyle(String(value ?? 'unknown'))"
              data-darkreader-ignore
            >
              {{ formatInvoiceStatus(String(value ?? 'unknown')) }}
            </span>
          </template>
        </data-table>
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
    :per-page-choices="[10, 30, 50, 100]"
  />

  <modal v-model:open="testEmailModalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" @cancel="closeTestEmailModal()">
        <h1>{{ t('invoicing.sendTestEmail') }}</h1>
      </modal-header>
    </template>
    <div class="mt-8">
      <div class="flex flex-col w-full">
        <div class="flex flex-col gap-y-5">
          <multiselect
            v-model="selectedUserEmail"
            :label="t('invoicing.selectRecipient')"
            :options="invoicingUserOptions"
            searchable
            :can-clear="false"
            :can-deselect="false"
            :create-option="false"
          />
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :disabled="!selectedUserEmail || sendingTestEmail"
            :busy="sendingTestEmail"
            @click="confirmSendTestEmail"
          >
            {{ t('invoicing.send') }}
          </custom-button>
        </div>
      </div>
    </div>
  </modal>

  <modal v-model:open="reimportModalOpen" :width="900">
    <template #header>
      <modal-header variant="cross" :saving="reimporting" @cancel="closeReimportModal">
        <h1>{{ t('invoicing.reimport') }}</h1>
      </modal-header>
    </template>
    <div class="mt-8">
      <div class="text-left mb-8">
        <span class="text-gray-500">
          {{ t('invoicing.reimportConfirm') }}
        </span>
      </div>
      <div class="w-full text-center flex justify-center gap-4">
        <custom-button variant="secondary" :disabled="reimporting" @click="closeReimportModal">
          {{ t('invoicing.cancel') }}
        </custom-button>
        <custom-button
          variant="primary"
          :busy="reimporting"
          :disabled="reimporting"
          @click="confirmReimportInvoice"
        >
          {{ t('invoicing.reimport') }}
        </custom-button>
      </div>
    </div>
  </modal>

  <modal v-model:open="statusModalOpen" :width="900">
    <template #header>
      <modal-header variant="cross" :saving="updatingStatus" @cancel="closeStatusModal">
        <h1>{{ t('invoicing.editStatus') }}</h1>
      </modal-header>
    </template>
    <div class="mt-8">
      <div class="text-left mb-6">
        <p class="text-gray-700 mb-4">
          {{ t('invoicing.editStatusConfirm') }}
        </p>
        <div v-if="selectedInvoiceForStatus" class="bg-gray-50 p-4 rounded-lg">
          <div class="mb-3">
            <span class="font-semibold text-gray-600 block text-sm mb-1">
              {{ t('invoicing.recipient') }}:
            </span>
            <span class="text-gray-900">{{ selectedInvoiceForStatus.customerName || '-' }}</span>
            <span class="text-gray-500 text-sm">
              ({{ selectedInvoiceForStatus.customerEmail || '-' }})
            </span>
          </div>
          <multiselect
            v-model="selectedEditableStatus"
            :label="t('invoicing.status')"
            :options="editableStatusOptions"
            searchable
            :can-clear="false"
            :can-deselect="false"
            :create-option="false"
          />
        </div>
      </div>
      <div class="w-full text-center flex justify-center gap-4">
        <custom-button variant="secondary" :disabled="updatingStatus" @click="closeStatusModal">
          {{ t('invoicing.cancel') }}
        </custom-button>
        <custom-button
          variant="primary"
          :busy="updatingStatus"
          :disabled="updatingStatus"
          @click="confirmUpdateStatus"
        >
          {{ t('invoicing.saveStatus') }}
        </custom-button>
      </div>
    </div>
  </modal>

  <modal v-model:open="sendInvoiceModalOpen" :width="800">
    <template #header>
      <modal-header variant="cross" @cancel="closeSendInvoiceModal">
        <h1>{{ t('invoicing.sendInvoice') }}</h1>
      </modal-header>
    </template>
    <div class="mt-8">
      <div class="text-left mb-6">
        <p class="text-gray-700 mb-4">
          {{ t('invoicing.sendInvoiceConfirm') }}
        </p>
        <div v-if="selectedInvoiceForSend" class="bg-gray-50 p-4 rounded-lg">
          <div class="mb-3">
            <span class="font-semibold text-gray-600 block text-sm mb-1">
              {{ t('invoicing.recipient') }}:
            </span>
            <span class="text-gray-900">{{ selectedInvoiceForSend.customerName || '-' }}</span>
            <span class="text-gray-500 text-sm">
              ({{ selectedInvoiceForSend.customerEmail || '-' }})
            </span>
          </div>
          <div>
            <span class="font-semibold text-gray-600 block text-sm mb-1">
              {{ t('invoicing.cc') }}:
            </span>
            <div class="flex flex-col gap-1">
              <div class="text-gray-900">
                <span class="text-gray-900">Working Bicycle AG</span>
                <span class="text-gray-500 text-sm">(finanzen@workingbicycle.ch)</span>
              </div>
              <div v-if="selectedInvoiceForSend.salesManagerEmail" class="text-gray-900">
                <span class="text-gray-900">
                  {{ salesManagerDisplayName(selectedInvoiceForSend) }}
                </span>
                <span
                  v-if="showSalesManagerEmail(selectedInvoiceForSend)"
                  class="text-gray-500 text-sm"
                >({{ selectedInvoiceForSend.salesManagerEmail }})</span>
              </div>
              <div v-if="selectedInvoiceForSend.customerCcEmail" class="text-gray-900">
                <span class="text-gray-500 text-sm">
                  {{ selectedInvoiceForSend.customerCcEmail }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="w-full text-center flex justify-center gap-4">
        <custom-button
          variant="secondary"
          :disabled="sendingInvoiceId != null"
          @click="closeSendInvoiceModal"
        >
          {{ t('invoicing.cancel') }}
        </custom-button>
        <custom-button
          variant="primary"
          :busy="sendingInvoiceId != null"
          :disabled="sendingInvoiceId != null"
          @click="confirmSendInvoice"
        >
          {{ t('invoicing.send') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, onMounted, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable from '@utils/table/Table.vue';
import type { Action as ActionType, Column } from '@utils/table/Table.vue';
import type { Filter } from '@utils/filter/Filter.vue';
import { useTableFields } from '@hooks/table-configuration';
import { useQueryRef } from '@hooks/url';
import { useProvider } from '@utils/table/hooks/provider';
import type { Filter as ProviderFilter } from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import { fetchApi, fetchApiBase, useFetchAction } from '@hooks/fetch';
import { useConfirmPrompt } from '@utils/prompt/injects';
import { useNotify } from '@utils/notifications';
import CustomButton from '@utils/CustomButton.vue';
import AddIcon from '@images/panes/add.svg?component';
import Action from '@utils/toolbar/Action.vue';
import Modal from '@utils/Modal/Modal.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Multiselect from '@utils/Multiselect.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { useStore } from '@/store';

interface Invoice {
  id: string;
  salesforceOpportunityId: string | null;
  bexioStatus?: string | null;
  bexioStatusSyncedAt?: string | null;
  offerNumber: string | null;
  customerName: string | null;
  customerEmail: string | null;
  customerCcEmail: string | null;
  customerStreet: string | null;
  customerCity: string | null;
  customerPostalCode: string | null;
  customerCountry: string | null;
  customerLanguage: string | null;
  campaignTitle: string | null;
  campaignStartDate: string | null;
  totalAmount: number;
  taxCode: string | null;
  bexioInvoiceId: number | null;
  isManual: boolean;
  invoiceStatus?: string | null;
  salesManagerName: string | null;
  salesManagerEmail: string | null;
  reference: string | null;
  sentAt: string | null;
  importedAt: string | null;
  bexioCreatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface InvoiceResponse {
  items: Invoice[];
  total: number;
}

interface InvoicingUser {
  id: string;
  fullName: string;
  email: string;
}

interface InvoiceLinkConfig {
  salesforceBaseUrl: string;
  bexioBaseUrl: string;
}

const editableInvoiceStatuses = [
  'draft',
  'pending',
  'paid',
  'partially_paid',
  'reminded',
  'cancelled',
  'overdue',
  'imported',
  'unknown',
] as const;

type EditableInvoiceStatus = (typeof editableInvoiceStatuses)[number];

const { t } = useI18n();
const store = useStore();
const canRunInvoiceGeneration = computed(() =>
  store.state.auth.user?.roles?.includes('ROLE_DEVELOPER') ?? false);

type InvoiceLifecycleTab =
  | 'all'
  | 'manual'
  | 'imported'
  | 'draft'
  | 'pending'
  | 'sent'
  | 'reminded'
  | 'partially_paid'
  | 'paid'
  | 'cancelled'
  | 'overdue'
  | 'unknown';

interface Props {
  variant?: InvoiceLifecycleTab;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'all',
});

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);
const filter = useQueryRef<ProviderFilter>('filter', {} as ProviderFilter);

const providerParams = computed(() => ({
  sort: {
    sortKey: sortKey.value,
    sortDirection: sortDirection.value,
  },
  filter: filter.value,
  special: {
    status: props.variant,
  },
  pagination: {
    page: page.value,
    perPage: perPage.value,
  },
}));

const {
  data: response,
  loading,
  refetch,
} = useProvider<InvoiceResponse>('/invoices', providerParams);

const notify = useNotify();
const confirm = useConfirmPrompt();
const [execGenerate, { loading: generating }] = useFetchAction();

const generateInvoices = async () => {
  try {
    await execGenerate('/invoices/generate', { method: 'POST' });
    notify?.({ variant: 'success', title: t('invoicing.generateSuccess') });
    refetch();
  } catch (e) {
    notify?.({ variant: 'error', title: t('invoicing.generateError') });
  }
};

const [execImport, { loading: importing }] = useFetchAction();

const importInvoices = async () => {
  try {
    await execImport('/invoices/import', { method: 'POST' });
    notify?.({ variant: 'success', title: t('invoicing.importSuccess') });
    refetch();
  } catch (e) {
    notify?.({ variant: 'error', title: t('invoicing.importError') });
  }
};

const testEmailModalOpen = ref(false);
const selectedUserEmail = ref('');
const selectedInvoiceId = ref<string | null>(null);
const invoicingUsers = ref<InvoicingUser[]>([]);
const invoicingUserOptions = computed(() =>
  invoicingUsers.value.map((user) => ({
    label: `${user.fullName} (${user.email})`,
    value: user.email,
  })));
const sendingTestEmail = ref(false);
const loadingPdfId = ref<string | null>(null);

const normalizeBaseUrl = (url: string): string => url.replace(/\/+$/, '');

const linkConfig = ref<InvoiceLinkConfig>({
  salesforceBaseUrl: 'https://d1t000000e0ydeak.my.salesforce.com',
  bexioBaseUrl: 'https://office.bexio.com',
});

const salesforceBaseUrl = computed(() => normalizeBaseUrl(linkConfig.value.salesforceBaseUrl));
const bexioBaseUrl = computed(() => normalizeBaseUrl(linkConfig.value.bexioBaseUrl));

const getSalesforceOpportunityLink = (item: Invoice) =>
  (item.salesforceOpportunityId
    ? `${salesforceBaseUrl.value}/lightning/r/Opportunity/${item.salesforceOpportunityId}/view`
    : '');

const getBexioInvoiceLink = (item: Invoice) =>
  (item.bexioInvoiceId
    ? `${bexioBaseUrl.value}/index.php/kb_invoice/show/id/${item.bexioInvoiceId}`
    : '');

onMounted(async () => {
  // eslint-disable-next-line no-use-before-define
  if (!visibleColumns.value.invoiceStatus) {
    // eslint-disable-next-line no-use-before-define
    visibleColumns.value = {
      // eslint-disable-next-line no-use-before-define
      ...visibleColumns.value,
      invoiceStatus: true,
    };
  }

  try {
    const [users, config] = await Promise.all([
      fetchApi<InvoicingUser[]>('/invoices/users'),
      fetchApi<InvoiceLinkConfig>('/invoices/link-config'),
    ]);
    invoicingUsers.value = users;
    linkConfig.value = {
      salesforceBaseUrl: config.salesforceBaseUrl || linkConfig.value.salesforceBaseUrl,
      bexioBaseUrl: config.bexioBaseUrl || linkConfig.value.bexioBaseUrl,
    };
  } catch (e) {
    console.error('Failed to fetch invoicing users or link config', e);
  }
});

const [execTestEmail] = useFetchAction();
const [execReimport, { loading: reimporting }] = useFetchAction();
const [execSendInvoice] = useFetchAction();
const [execUpdateStatus, { loading: updatingStatus }] = useFetchAction();
const [execUnmarkManual, { loading: unmarkingManual }] = useFetchAction();
const [execMarkManual, { loading: markingManual }] = useFetchAction();
const sendingInvoiceId = ref<string | null>(null);
const reimportModalOpen = ref(false);
const reimportInvoiceId = ref<string | null>(null);
const statusModalOpen = ref(false);
const selectedInvoiceForStatus = ref<Invoice | null>(null);
const selectedEditableStatus = ref<EditableInvoiceStatus>('unknown');
const sendInvoiceModalOpen = ref(false);
const selectedInvoiceForSend = ref<Invoice | null>(null);
const editableInvoiceStatusSet = new Set<EditableInvoiceStatus>(editableInvoiceStatuses);
const editableStatusOptions = computed(() =>
  editableInvoiceStatuses.map((status) => ({
    // eslint-disable-next-line no-use-before-define
    label: formatInvoiceStatus(status),
    value: status,
  })));

const closeTestEmailModal = () => {
  testEmailModalOpen.value = false;
  selectedUserEmail.value = '';
  selectedInvoiceId.value = null;
};

const openReimportModal = (item: Invoice) => {
  reimportInvoiceId.value = item.id;
  reimportModalOpen.value = true;
};

const closeReimportModal = () => {
  if (reimporting.value) return;
  reimportModalOpen.value = false;
  reimportInvoiceId.value = null;
};

const resolveEditableStatus = (item: Invoice): EditableInvoiceStatus => {
  const bexioStatus = item.bexioStatus?.toLowerCase();
  if (bexioStatus && editableInvoiceStatusSet.has(bexioStatus as EditableInvoiceStatus)) {
    return bexioStatus as EditableInvoiceStatus;
  }

  // eslint-disable-next-line no-use-before-define
  const normalizedInvoiceStatus = normalizeInvoiceStatus(item);
  if (editableInvoiceStatusSet.has(normalizedInvoiceStatus as EditableInvoiceStatus)) {
    return normalizedInvoiceStatus as EditableInvoiceStatus;
  }

  if (item.bexioInvoiceId == null) {
    return 'imported';
  }

  return 'unknown';
};

const openStatusModal = (item: Invoice) => {
  selectedInvoiceForStatus.value = item;
  selectedEditableStatus.value = resolveEditableStatus(item);
  statusModalOpen.value = true;
};

const closeStatusModal = () => {
  if (updatingStatus.value) return;
  statusModalOpen.value = false;
  selectedInvoiceForStatus.value = null;
  selectedEditableStatus.value = 'unknown';
};

const confirmUpdateStatus = async () => {
  if (!selectedInvoiceForStatus.value) return;

  try {
    await execUpdateStatus(`/invoices/${selectedInvoiceForStatus.value.id}/status`, {
      method: 'PATCH',
      body: { status: selectedEditableStatus.value },
    });
    notify?.({ variant: 'success', title: t('invoicing.editStatusSuccess') });
    closeStatusModal();
    await refetch();
  } catch (e) {
    notify?.({ variant: 'error', title: t('invoicing.editStatusError') });
  }
};

const unmarkManual = async (item: Invoice) => {
  const answer = await confirm?.(t('invoicing.unmarkManualConfirm'));
  if (!answer) return;

  try {
    await execUnmarkManual(`/invoices/${item.id}/unmark-manual`, { method: 'POST' });
    notify?.({ variant: 'success', title: t('invoicing.unmarkManualSuccess') });
    refetch();
  } catch (e) {
    notify?.({ variant: 'error', title: t('invoicing.unmarkManualError') });
  }
};

const markManual = async (item: Invoice) => {
  const answer = await confirm?.(t('invoicing.markManualConfirm'));
  if (!answer) return;

  try {
    await execMarkManual(`/invoices/${item.id}/mark-manual`, { method: 'POST' });
    notify?.({ variant: 'success', title: t('invoicing.markManualSuccess') });
    refetch();
  } catch (e) {
    notify?.({ variant: 'error', title: t('invoicing.markManualError') });
  }
};

const confirmReimportInvoice = async () => {
  if (!reimportInvoiceId.value) return;

  try {
    await execReimport(`/invoices/${reimportInvoiceId.value}/reimport`, { method: 'POST' });
    notify?.({ variant: 'success', title: t('invoicing.reimportSuccess') });
    closeReimportModal();
    refetch();
  } catch (e) {
    notify?.({ variant: 'error', title: t('invoicing.reimportError') });
  }
};

const sendInvoiceToCustomer = async (item: Invoice) => {
  selectedInvoiceForSend.value = item;
  sendInvoiceModalOpen.value = true;
};

const closeSendInvoiceModal = () => {
  if (sendingInvoiceId.value) return;
  sendInvoiceModalOpen.value = false;
  selectedInvoiceForSend.value = null;
};

const confirmSendInvoice = async () => {
  if (!selectedInvoiceForSend.value) return;
  sendingInvoiceId.value = selectedInvoiceForSend.value.id;
  try {
    await execSendInvoice(`/invoices/${selectedInvoiceForSend.value.id}/send`, { method: 'POST' });
    notify?.({ variant: 'success', title: t('invoicing.sendInvoiceSuccess') });
    closeSendInvoiceModal();
    refetch();
  } catch (e) {
    notify?.({ variant: 'error', title: t('invoicing.sendInvoiceError') });
  } finally {
    sendingInvoiceId.value = null;
  }
};

const deriveInvoiceStatus = (item: Invoice): string => {
  if (item.sentAt) return 'sent';
  if (item.bexioInvoiceId != null) return 'draft';
  return 'imported';
};

const salesManagerDisplayName = (item: Invoice): string =>
  item.salesManagerName || item.salesManagerEmail || '';

const showSalesManagerEmail = (item: Invoice): boolean =>
  !!item.salesManagerName && item.salesManagerName !== item.salesManagerEmail;

const normalizeInvoiceStatus = (item: Invoice): string => {
  const candidate =
    item.invoiceStatus ?? (item as unknown as { InvoiceStatus?: string }).InvoiceStatus;
  if (candidate && typeof candidate === 'string') return candidate.toLowerCase();
  if (item.bexioStatus && typeof item.bexioStatus === 'string') {
    return item.bexioStatus.toLowerCase();
  }

  return deriveInvoiceStatus(item);
};

const formatInvoiceStatus = (status: string): string => {
  const key = `invoicing.statusValues.${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
};

const formatCustomerLanguage = (value: string | null): string => {
  if (!value) return '-';
  const normalized = value.trim().toLowerCase();
  if (!normalized) return '-';

  const code = normalized.includes('-') ? normalized.split('-')[0] : normalized;
  const key = `language.${code}`;
  const translated = t(key);
  return translated === key ? value : translated;
};

const statusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
  case 'draft':
    return 'status-draft';
  case 'imported':
    return 'status-imported';
  case 'pending':
    return 'status-pending';
  case 'sent':
    return 'status-sent';
  case 'paid':
    return 'status-paid';
  case 'partially_paid':
    return 'status-partially-paid';
  case 'reminded':
    return 'status-reminded';
  case 'cancelled':
    return 'status-cancelled';
  case 'manual':
    return 'status-manual';
  case 'overdue':
    return 'status-overdue';
  default:
    return 'status-unknown';
  }
};

type BadgeStyle = {
  backgroundColor: string;
  color: string;
  borderColor: string;
};

const statusBadgeStyles: Record<string, BadgeStyle> = {
  draft: { backgroundColor: '#9ca3af', color: '#ffffff', borderColor: '#9ca3af' },
  imported: { backgroundColor: '#7c3aed', color: '#ffffff', borderColor: '#7c3aed' },
  pending: { backgroundColor: '#f59e0b', color: '#ffffff', borderColor: '#f59e0b' },
  sent: { backgroundColor: '#2563eb', color: '#ffffff', borderColor: '#2563eb' },
  paid: { backgroundColor: '#16a34a', color: '#ffffff', borderColor: '#16a34a' },
  partially_paid: { backgroundColor: '#059669', color: '#ffffff', borderColor: '#059669' },
  reminded: { backgroundColor: '#f97316', color: '#ffffff', borderColor: '#f97316' },
  cancelled: { backgroundColor: '#6b7280', color: '#ffffff', borderColor: '#6b7280' },
  manual: { backgroundColor: '#0ea5e9', color: '#ffffff', borderColor: '#0ea5e9' },
  overdue: { backgroundColor: '#dc2626', color: '#ffffff', borderColor: '#dc2626' },
  unknown: { backgroundColor: '#4b5563', color: '#ffffff', borderColor: '#4b5563' },
};

const statusBadgeStyle = (status: string): BadgeStyle => {
  const normalized = status.toLowerCase();
  return statusBadgeStyles[normalized] ?? statusBadgeStyles.unknown;
};

const actions: ActionType[] = [
  {
    title: t('invoicing.editStatus'),
    action: (item: Invoice) => openStatusModal(item),
  },
  {
    title: t('invoicing.unmarkManual'),
    action: (item: Invoice) => unmarkManual(item),
    condition: (item: Invoice) => item.isManual && !unmarkingManual.value,
  },
  {
    title: t('invoicing.markManual'),
    action: (item: Invoice) => markManual(item),
    condition: (item: Invoice) => !item.isManual && !markingManual.value,
  },
  {
    title: t('invoicing.reimport'),
    action: (item: Invoice) => openReimportModal(item),
    condition: (item: Invoice) =>
      item.sentAt == null ||
      normalizeInvoiceStatus(item) === 'imported' ||
      item.bexioInvoiceId == null,
  },
  {
    title: t('invoicing.viewPdf'),
    action: async (item: Invoice) => {
      loadingPdfId.value = item.id;
      notify?.({ variant: 'success', title: t('invoicing.loadingPdf') });
      try {
        const response2 = await fetchApiBase(`/invoices/${item.id}/pdf`);
        const blob = await response2.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (e) {
        notify?.({ variant: 'error', title: t('invoicing.viewPdfError') });
      } finally {
        loadingPdfId.value = null;
      }
    },
    condition: (item: Invoice) => item.bexioInvoiceId != null && loadingPdfId.value !== item.id,
  },
  {
    title: t('invoicing.sendTestEmail'),
    action: (item: Invoice) => {
      selectedInvoiceId.value = item.id;
      selectedUserEmail.value = '';
      testEmailModalOpen.value = true;
    },
    condition: (item: Invoice) => item.bexioInvoiceId != null,
  },
  {
    title: t('invoicing.sendInvoice'),
    action: (item: Invoice) => sendInvoiceToCustomer(item),
    condition: (item: Invoice) =>
      item.bexioInvoiceId != null && item.sentAt == null && sendingInvoiceId.value !== item.id,
  },
];

const confirmSendTestEmail = async () => {
  if (!selectedInvoiceId.value || !selectedUserEmail.value) return;
  sendingTestEmail.value = true;
  try {
    await execTestEmail(`/invoices/${selectedInvoiceId.value}/test-email`, {
      method: 'POST',
      body: { email: selectedUserEmail.value },
    });
    notify?.({ variant: 'success', title: t('invoicing.sendTestEmailSuccess') });
    closeTestEmailModal();
  } catch (e) {
    notify?.({ variant: 'error', title: t('invoicing.sendTestEmailError') });
  } finally {
    sendingTestEmail.value = false;
  }
};

const records = computed<Invoice[]>(() => {
  const items = unref(unref(response))?.items ?? [];
  return items.map((item) => ({
    ...item,
    invoiceStatus: normalizeInvoiceStatus(item),
  }));
});
const total = computed(() => unref(unref(response))?.total ?? 0);

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'offerNumber',
    title: t('invoicing.offerNumber'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    link: getSalesforceOpportunityLink,
    linkTarget: '_blank',
    filter: {
      type: 'text',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.offerNumber'),
      },
    },
  },
  {
    key: 'customerName',
    title: t('invoicing.customerName'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.customerName'),
      },
    },
  },
  {
    key: 'customerLanguage',
    title: t('invoicing.customerLanguage'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.customer'),
    formatter: (v: string | null) => formatCustomerLanguage(v),
    filter: {
      type: 'dropdown',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.customerLanguage'),
        options: [
          { label: 'DE', value: 'DE' },
          { label: 'FR', value: 'FR' },
          { label: 'EN', value: 'EN' },
        ],
      },
    },
  },
  {
    key: 'campaignTitle',
    title: t('invoicing.campaignTitle'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.campaignTitle'),
      },
    },
  },
  {
    key: 'totalAmount',
    title: t('invoicing.totalAmount'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    formatter: (v: number) => (v != null ? `CHF ${v.toFixed(2)}` : '-'),
    link: getBexioInvoiceLink,
    linkTarget: '_blank',
  },
  {
    key: 'salesManagerName',
    title: t('invoicing.salesManager'),
    sortable: false,
    visible: false,
    group: t('invoicing.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.salesManager'),
      },
    },
  },
  {
    key: 'isManual',
    title: t('invoicing.isManual'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    formatter: (v: boolean) => (v ? t('invoicing.yes') : t('invoicing.no')),
    filter: {
      type: 'dropdown',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.isManual'),
        options: [
          { label: t('invoicing.yes'), value: 'true' },
          { label: t('invoicing.no'), value: 'false' },
        ],
      },
    },
  },
  {
    key: 'invoiceStatus',
    title: t('invoicing.status'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    formatter: (v: string) => formatInvoiceStatus(v),
  },
  {
    key: 'sentAt',
    title: t('invoicing.sentAt'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    filter: {
      key: 'isSent',
      type: 'dropdown',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.isSent'),
        options: [
          { label: t('invoicing.yes'), value: 'true' },
          { label: t('invoicing.no'), value: 'false' },
        ],
      },
    },
  },
  {
    key: 'importedAt',
    title: t('invoicing.importedAt'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
  },
  {
    key: 'bexioInvoiceId',
    title: t('invoicing.bexioInvoice'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.links'),
    formatter: (v: number | null) => (v != null ? `#${v}` : '-'),
    link: getBexioInvoiceLink,
    linkTarget: '_blank',
  },
  {
    key: 'salesforceOpportunityId',
    title: t('invoicing.salesforceOpportunity'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.links'),
    formatter: (v: string | null) => (v ? v.substring(0, 15) : '-'),
    link: getSalesforceOpportunityLink,
    linkTarget: '_blank',
    filter: {
      type: 'text',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.salesforceOpportunity'),
      },
    },
  },
  {
    key: 'campaignStartDate',
    title: t('invoicing.campaignStartDate'),
    sortable: false,
    visible: false,
    group: t('invoicing.fieldGroups.general'),
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy') : '-'),
  },
  {
    key: 'reference',
    title: t('invoicing.reference'),
    sortable: false,
    visible: false,
    group: t('invoicing.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.reference'),
      },
    },
  },
  {
    key: 'customerEmail',
    title: t('invoicing.customerEmail'),
    sortable: false,
    visible: false,
    group: t('invoicing.fieldGroups.customer'),
    filter: {
      type: 'text',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.customerEmail'),
      },
    },
  },
  {
    key: 'customerCcEmail',
    title: t('invoicing.customerCcEmail'),
    sortable: false,
    visible: false,
    group: t('invoicing.fieldGroups.customer'),
    filter: {
      type: 'text',
      group: t('invoicing.filterGroups.general'),
      options: {
        label: t('invoicing.customerCcEmail'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    sortable: false,
    visible: true,
    group: t('invoicing.fieldGroups.general'),
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
  },
];

const visibleColumns = useTableFields(columns);

const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid transparent;
}

.status-draft {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

.status-imported {
  background: #ede9fe;
  color: #4c1d95;
  border-color: #c4b5fd;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
  border-color: #fcd34d;
}

.status-sent {
  background: #dbeafe;
  color: #1e40af;
  border-color: #93c5fd;
}

.status-paid {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}

.status-partially-paid {
  background: #d1fae5;
  color: #065f46;
  border-color: #6ee7b7;
}

.status-reminded {
  background: #ffedd5;
  color: #9a3412;
  border-color: #fdba74;
}

.status-cancelled {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

.status-manual {
  background: #e0f2fe;
  color: #075985;
  border-color: #7dd3fc;
}

.status-overdue {
  background: #fee2e2;
  color: #7f1d1d;
  border-color: #fca5a5;
}

.status-unknown {
  background: #f9fafb;
  color: #111827;
  border-color: #e5e7eb;
}
</style>
